import axios from "../../../../services/axiosConfig";
import api from "../../../../config/gatewayConfig";
import { normalizeCellMatchMode } from "../utils/cellMatchModeUtils";
import { serializeColumnDefaultGroups } from "../utils/columnDefaultRulesUtils";
import { buildColumnFiltersPayload } from "../utils/columnFilterUtils";

const authHeaders = () => ({
  Authorization: `Bearer ${JSON.parse(sessionStorage.getItem("token"))}`,
});

const requestConfig = (extra = {}) => ({
  headers: authHeaders(),
  skipGlobalErrorHandler: true,
  ...extra,
});

const extractErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) {
    return data;
  }
  return data?.detail
    || data?.message
    || data?.error
    || error?.message
    || fallback;
};

export const searchCatalogVendors = async (query = "", stato = null) => {
  try {
    const payload = { query };
    if (stato != null) {
      payload.stato = stato;
    }
    const response = await axios.post(api.catalogMappingRicerca, payload, requestConfig());
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento dei vendor catalogo"));
  }
};

const findVendorById = (items, vendorId) => (
  (items || []).find((item) => Number(item.vendor_id) === Number(vendorId)) || null
);

export const getCatalogVendor = async (vendorId) => {
  const normalizedVendorId = Number(vendorId);
  if (!Number.isFinite(normalizedVendorId)) {
    throw new Error("Id vendor non valido");
  }

  try {
    const response = await axios.post(
      api.catalogMappingVendorDetail,
      { vendorId: normalizedVendorId },
      requestConfig(),
    );
    return response.data;
  } catch (detailError) {
    const status = detailError?.response?.status;
    if (status && status !== 404 && status !== 500 && status !== 503) {
      throw new Error(extractErrorMessage(detailError, "Errore nel caricamento del vendor"));
    }
  }

  try {
    const items = await searchCatalogVendors(String(normalizedVendorId));
    const vendor = findVendorById(items, normalizedVendorId);
    if (!vendor) {
      throw new Error("Vendor non trovato");
    }
    return vendor;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento del vendor"));
  }
};

export const getCatalogMapping = async (vendorId) => {
  try {
    const response = await axios.post(api.catalogMappingGet, { vendorId }, requestConfig());
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento del mapping"));
  }
};

export const saveCatalogMapping = async (vendorId, payload) => {
  try {
    const response = await axios.post(api.catalogMappingSave, { vendorId, ...payload }, requestConfig());
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel salvataggio del mapping"));
  }
};

export const previewCatalogFeed = async (
  vendorId,
  limit = 20,
  offset = 0,
  problemFilter = "all",
  options = {},
) => {
  try {
    const payload = {
      vendorId,
      limit,
      offset,
      problem_filter: problemFilter,
    };
    if (options.skipMatchesOnly) {
      payload.skip_matches_only = true;
      payload.row_skip = (options.rowSkip || []).map((rule) => ({
        column: rule.column,
        value: rule.value == null ? "" : String(rule.value),
        mode: rule.mode || "eq",
      }));
    }
    if (options.columnDefaultMatchesOnly) {
      payload.column_default_matches_only = true;
      payload.column_defaults = serializeColumnDefaultGroups(options.columnDefaults || {});
    }
    const response = await axios.post(
      api.catalogMappingPreview,
      payload,
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nella preview del feed"));
  }
};

export const getCatalogOutput = async (
  vendorId,
  offset = 0,
  limit = 50,
  errorsOnly = false,
  filters = {},
  hardDeleteRules = [],
  hardDeleteMatchesOnly = false,
) => {
  try {
    const payload = {
      vendorId,
      offset,
      limit,
      errors_only: errorsOnly,
    };
    if (filters.uncategorizedOnly) {
      payload.uncategorized_only = true;
    }
    if (filters.isDisattivato !== undefined && filters.isDisattivato !== "") {
      payload.is_disattivato = Number(filters.isDisattivato);
    }
    if (filters.isDeleted !== undefined && filters.isDeleted !== "") {
      payload.is_deleted = Number(filters.isDeleted);
    }
    if (filters.certificato === "true") {
      payload.certificato = true;
    } else if (filters.certificato === "false") {
      payload.certificato = false;
    }
    if (filters.stato !== undefined && filters.stato !== "") {
      payload.stato = Number(filters.stato);
    }
    if (filters.productCodeFilter) {
      payload.product_code_filter = String(filters.productCodeFilter).trim();
    }
    const columnFilters = buildColumnFiltersPayload(filters.columnFilters);
    if (columnFilters.length > 0) {
      payload.column_filters = columnFilters;
    }
    if (hardDeleteMatchesOnly && Array.isArray(hardDeleteRules) && hardDeleteRules.length > 0) {
      payload.hard_delete_matches_only = true;
      payload.hard_delete_rules = hardDeleteRules.map((rule) => ({
        table: rule.table,
        column: rule.column,
        value: rule.value == null ? "" : String(rule.value),
        mode: normalizeCellMatchMode(rule.mode || "eq"),
      }));
    }
    const response = await axios.post(
      api.catalogMappingCatalog,
      payload,
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento del catalogo normalizzato"));
  }
};

export const saveCatalogCell = async (vendorId, payload) => {
  try {
    const body = { vendorId, ...payload };
    if (payload.catalog_list_id != null) {
      body.catalog_list_id = payload.catalog_list_id;
    }
    if (payload.product_list_id != null) {
      body.product_list_id = payload.product_list_id;
    }
    const response = await axios.post(
      api.catalogMappingCatalogCell,
      body,
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel salvataggio della cella"));
  }
};

export const bulkCategorizeByAlbero = async (vendorId, { alberoValue, categoryListId, mappings = null }) => {
  try {
    const payload = { vendorId };
    if (Array.isArray(mappings) && mappings.length > 0) {
      payload.mappings = mappings.map((rule) => ({
        albero_value: rule.alberoValue,
        category_list_id: Number(rule.categoryId),
      }));
    } else {
      payload.albero_value = alberoValue;
      payload.category_list_id = categoryListId;
    }
    const response = await axios.post(
      api.catalogMappingBulkCategorize,
      payload,
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nella categorizzazione multipla"));
  }
};

export const hardDeleteCatalogRows = async (vendorId, { table, column, value, mode = "eq", rules = null }) => {
  try {
    const payload = {
      vendorId,
    };
    if (Array.isArray(rules) && rules.length > 0) {
      payload.rules = rules.map((rule) => ({
        table: rule.table,
        column: rule.column,
        value: rule.value == null ? "" : String(rule.value),
        mode: normalizeCellMatchMode(rule.mode || "eq"),
      }));
    } else {
      payload.table = table;
      payload.column = column;
      payload.value = value == null ? "" : String(value);
      payload.mode = normalizeCellMatchMode(mode || "eq");
    }
    const response = await axios.post(
      api.catalogMappingHardDelete,
      payload,
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nell'eliminazione fisica cataloghi"));
  }
};

export const getCatalogFieldKeys = async () => {
  try {
    const response = await axios.post(api.catalogMappingFieldKeys, {}, requestConfig());
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento dei campi mapping"));
  }
};

export const getCatalogCategories = async () => {
  try {
    const response = await axios.post(api.catalogMappingCategories, {}, requestConfig());
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento delle categorie"));
  }
};

export const getRawFeed = async (vendorId, offset = 0, limit = 100) => {
  try {
    const response = await axios.post(
      api.catalogMappingRawFeed,
      { vendorId, offset, limit },
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento del feed raw"));
  }
};

export const downloadRawFeed = async (vendorId) => {
  try {
    const response = await axios.post(
      api.catalogMappingRawDownload,
      { vendorId },
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel download del feed raw"));
  }
};

export const uploadRawFeed = async (vendorId, file) => {
  try {
    const formData = new FormData();
    formData.append("vendorId", String(vendorId));
    formData.append("file", file);
    const response = await axios.post(
      api.catalogMappingRawUpload,
      formData,
      requestConfig({
        timeout: 30 * 60 * 1000,
        headers: authHeaders(),
      }),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento manuale del feed raw"));
  }
};

export const normalizeCatalogFeed = async (vendorId, feedConfig, limit = 20, options = {}) => {
  try {
    const response = await axios.post(
      api.catalogMappingNormalize,
      {
        vendorId,
        feed_config: feedConfig,
        limit,
        auto_map: Boolean(options.autoMap),
      },
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nella normalizzazione del feed"));
  }
};

export const saveNormalizedCell = async (vendorId, payload) => {
  try {
    const response = await axios.post(
      api.catalogMappingNormalizedCell,
      { vendorId, ...payload },
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel salvataggio della cella normalizzata"));
  }
};

export const deleteNormalizedRow = async (vendorId, rowIndex) => {
  try {
    const response = await axios.post(
      api.catalogMappingNormalizedRowDelete,
      { vendorId, row_index: rowIndex },
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nell'eliminazione della riga"));
  }
};

export const startCatalogImport = async (vendorIds = null, options = {}) => {
  const useStoredFeed = Boolean(options.useStoredFeed);
  const allowedTypes = new Set([
    "manual",
    "scheduled",
    "manual_all",
    "manual_selected",
    "manual_single",
    "manual_stored",
  ]);
  const importType = allowedTypes.has(options.importType) ? options.importType : "manual_single";
  try {
    const response = await axios.post(
      api.catalogImportStart,
      { vendor_ids: vendorIds, use_stored_feed: useStoredFeed, import_type: importType },
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nell'avvio import cataloghi"));
  }
};

export const getCatalogImportLogs = async (jobId, since = 0) => {
  try {
    const payload = { since };
    if (jobId) {
      payload.job_id = jobId;
    }
    const response = await axios.post(api.catalogImportLogs, payload, requestConfig());
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel recupero log import"));
  }
};

export const getCatalogImportStatus = async (jobId = null) => {
  const status = await getCatalogServiceStatus(jobId);
  return status?.import_job || status;
};

export const getCatalogServiceStatus = async (jobId = null) => {
  try {
    const payload = jobId ? { job_id: jobId } : {};
    const response = await axios.post(api.catalogServiceStatus, payload, requestConfig());
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel recupero stato servizio cataloghi"));
  }
};

export const cancelCatalogImport = async (jobId) => {
  try {
    const response = await axios.post(
      api.catalogImportCancel,
      { job_id: jobId },
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nell'arresto dell'import"));
  }
};

export const getCatalogServiceConfig = async () => {
  try {
    const response = await axios.post(api.catalogServiceConfig, {}, requestConfig());
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento configurazione servizio"));
  }
};

export const updateCatalogServiceConfig = async (payload) => {
  try {
    const response = await axios.post(api.catalogServiceConfigUpdate, payload, requestConfig());
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel salvataggio configurazione servizio"));
  }
};

export const previewProductCodeRegen = async (vendorId, options = {}) => {
  try {
    const response = await axios.post(
      api.catalogMappingProductCodeRegenPreview,
      {
        vendorId: Number(vendorId),
        code_source: options.codeSource || "internal",
        match_logic: options.matchLogic || "and",
        match_rules: (options.matchRules || []).map((rule) => ({
          feed_field: rule.feed_field,
          db_column: rule.db_column,
        })),
      },
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore simulazione rigenerazione codice prodotto"));
  }
};

export const applyProductCodeRegen = async (vendorId, options = {}) => {
  try {
    const response = await axios.post(
      api.catalogMappingProductCodeRegenApply,
      {
        vendorId: Number(vendorId),
        code_source: options.codeSource || "internal",
        match_logic: options.matchLogic || "and",
        match_rules: (options.matchRules || []).map((rule) => ({
          feed_field: rule.feed_field,
          db_column: rule.db_column,
        })),
      },
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore applicazione rigenerazione codice prodotto"));
  }
};

export const getProductCodeRegenOptions = async (vendorId) => {
  try {
    const response = await axios.post(
      api.catalogMappingProductCodeRegenOptions,
      { vendorId: Number(vendorId) },
      requestConfig(),
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore caricamento opzioni rigenerazione codice prodotto"));
  }
};
